import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfRect,
  PdfPoint,
  PdfBrush,
  PdfFont,
  ETextAlign,
} from '../../pdf/pdf';
import { ILayoutImageGen } from '../layoutImage';
import { IItemCount } from '@educraft/base';
import { OptsImage } from '../optsImage';
import { GUtil } from '../../pdf/gutil';
import { calcOrigin } from '../../core/genHelper';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/image';
import '../../pdf/draw/shapes';

/**
 * Pocty__2 (Counting 2) - Number representation table
 *
 * Layout: 1 portrait page, 9×10 grid.
 * Each row (1-10): images in grid, dice, digit, letter, Roman numeral,
 * addition, subtraction, multiplication, square root.
 *
 * Migrated from C# EduFrame.Img10.Generators.Pocty__2
 */
export class Pocty__2 implements ILayoutImageGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 2;
  }
  countMax(): number {
    return 12;
  }
  public async generate(pdf: PdfGen, opts: OptsImage): Promise<void> {
    const margin = PdfUnit.FromMillimeters(5);
    const diceMargin = PdfUnit.FromMillimeters(1);
    const inflateMargin = PdfUnit.FromMillimeters(2);

    const random = opts.data?.random;
    const safeRandom = {
      next: (min: number, max: number): number => {
        if (!random) return min;
        return random.next(min, max);
      },
    };

    const cols: ((num: number) => string)[] = [
      (num) => `${num}`,
      (num) => ['', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L'][num],
      (num) => ['', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII'][num],
      (num) => {
        if (num <= 1) return '1 + 0';
        const a = 1 + safeRandom.next(0, num - 1);
        return `${a} + ${num - a}`;
      },
      (num) => {
        if (num <= 0) return '0 - 0';
        const a = 1 + safeRandom.next(0, num);
        return `${num + a} - ${a}`;
      },
      (num) =>
        [
          '',
          '1 \u00d7 1',
          '2 \u00d7 1',
          '3 \u00d7 1',
          '2 \u00d7 2',
          '5 \u00d7 1',
          '2 \u00d7 3',
          '7 \u00d7 1',
          '2 \u00d7 4',
          '3 \u00d7 3',
          '2 \u00d7 5',
          '11 \u00d7 1',
          '3 \u00d7 4',
        ][num],
      // num => `\u221a${num * num}`,
    ];

    const { origin, size } = calcOrigin(cols.length + 2, opts.list.length, margin, PdfSize.A4);
    const bigFont = new PdfFont(PdfFont.FONT_NAME, 36);

    const page = pdf.addPage();

    const data = cols.map((fn) => Array.from({ length: opts.list.length }, (_, j) => fn(j + 1)));
    const fonts = data.map((arr) => page.adjustFont(size.width, arr, bigFont));

    const rect = (x: number, y: number) =>
      new PdfRect(origin.x + x * size.width, origin.y + y * size.height, size.width, size.height);

    for (let i = 0; i < opts.list.length; i++) {
      const num = i + 1;
      const { cx, cy } = calcSize(num);
      const r1 = rect(0, i);
      page.drawRectangle(GUtil.cutPen, null, r1);

      const s = { width: r1.width / cx, height: r1.height / cy };

      for (let x1 = 0; x1 < cx; x1++) {
        for (let y1 = 0; y1 < cy; y1++) {
          if (y1 * cx + x1 < num) {
            const r = new PdfRect(r1.x + s.width * x1, r1.y + s.height * y1, s.width, s.height);
            if (i < opts.list.length) {
              page.drawImageCenter(opts.list[i].image, r);
            }
          }
        }
      }

      const r2raw = rect(1, i);
      page.drawRectangle(GUtil.cutPen, null, r2raw);

      const r2 = new PdfRect(
        r2raw.x + inflateMargin,
        r2raw.y + inflateMargin,
        r2raw.width - 2 * inflateMargin,
        r2raw.height - 2 * inflateMargin
      );

      const dices: number[] = [];
      if (num <= 6) {
        dices.push(num);
      } else {
        dices.push(6);
        dices.push(num - 6);
      }

      page.drawDicesV(GUtil.basePen, r2, diceMargin, ...dices);

      for (let c = 0; c < cols.length; c++) {
        const r = rect(2 + c, i);
        const text = data[c][i];
        page.drawRectangle(GUtil.cutPen, null, r);
        page.drawString(text, fonts[c], PdfBrush.Black, null, r, ETextAlign.Center);
      }
    }
  }
}

function calcSize(num: number): { cx: number; cy: number } {
  const cx = Math.ceil(Math.sqrt(num));
  const cy = Math.ceil(num / cx);
  return { cx, cy };
}
